import { AtlasFlex, AtlasInfo, AtlasLoading, AtlasResultsHeader, AtlasRibbon, CampusModuleWithStages, CampusSort, CampusSortOption, CampusStageCard, CampusTag } from "atlas-ds"
import { default as NextLink } from "next/link"
import { useSearchParams } from "next/navigation"
import { useRouter } from "next/router"
import { EntrepriseContext } from "pages/_app"
import { RefObject, useContext } from "react"
import { fetchAPIModulesAndStages, keyAPIModulesAndStages } from "services/client"
import { BRANCHE_CAP, CAP_COMPETENCE_URL, DEFAULT_MODULES_PER_PAGE } from 'services/global'
import { Branche, Entreprise, Modalite, Module, ModulesFilters, Organisme, SearchContext, Stage, Thematique, Type } from "services/model"
import useFetchPagedFilteredAPI from "services/useFetchPagedFilteredAPI"
import { formatDuration, formatMoney, pathify } from "utils/format"
import { CofinancementTag } from "./cofinancement-tag"
import { expiresSoon } from "./derniers-jours"
import { Filters } from "./filters"
import Link from "./link"

interface ModulesProps {
    branches?: Branche[]
    thematique?: Thematique
    organisme?: Organisme
    types: Type[]
    modalites: Modalite[]
    searchInputRef?: RefObject<HTMLInputElement>
}

export function Modules(props: ModulesProps) {
    const searchParams = useSearchParams()
    const [entreprise] = useContext(EntrepriseContext)
    const router = useRouter()

    const context: SearchContext = {
        brancheId: router.pathname === CAP_COMPETENCE_URL
            ? BRANCHE_CAP
            : (entreprise as Entreprise)?.branche?.valueName ?? undefined,
        organisme: props.organisme
    }

    const { data, count, loading, updateFilters, resetFilters, hasActiveFilters } = useFetchPagedFilteredAPI<Module, ModulesFilters>(
        (authSession, authenticating, searchParams) => keyAPIModulesAndStages(authSession, authenticating, searchParams, context),
        (authSession, _authenticating, key) => fetchAPIModulesAndStages(authSession, key),
    )

    const getStageHref = (module: Module, stage: Stage) => {
        // Conserver uniquement les paramètres pertinents pour la page stage
        const params = new URLSearchParams(searchParams!)

        const paramsToRemove = ["page", "tri", "recherche", "branche", "domaine", "thematique", "module", "organisme"]

        paramsToRemove.forEach(param => params.delete(param))

        return `/stages/${pathify(module.label)}/${stage.sfid}${params.toString() ? `?${params.toString()}` : ''}`
    }

    const customHasActiveFilters = () => {
        return hasActiveFilters() || (router.pathname === CAP_COMPETENCE_URL && !entreprise)
    }

    const getStageCard = (module: Module, stage: Stage) => {
        const stageCard = <CampusStageCard
            key={stage.sfid}
            organism={stage.organisme.name}
            infos={[
                ...stage.prixInterRevise ? [{
                    label: "Inter",
                    value: formatMoney(stage.prixInterRevise)}
                ] : [],
                ...stage.prixIntraRevise ? [{
                    label: "Intra",
                    value: formatMoney(stage.prixIntraRevise)}
                ] : [],
            ]}
            link={<Link href={getStageHref(module, stage)}>
                {stage.nbSessions} session{(stage.nbSessions ?? 0) > 1 ? "s" : ""}
            </Link>}
        />

        return expiresSoon(stage.dateFin)
            ? <AtlasRibbon key={stage.sfid} label="Derniers jours" type="warning">{stageCard}</AtlasRibbon>
            : stageCard
    }

    const sortOptions: CampusSortOption[] = [
        {
            id: "date",
            label: "Sessions les plus proches",
            icon: "calendar"
        },
        {
            id: "module",
            label: "Nom du module",
            icon: "alphabet"
        }
    ]

    return <Filters
        count={count}
        pageSize={DEFAULT_MODULES_PER_PAGE}
        branches={props.branches ?? []}
        modalites={props.modalites ?? []}
        types={props.types ?? []}
        context={context}
        updateFilters={updateFilters}
        searchInputRef={props.searchInputRef}
    >
        <AtlasLoading loading={loading}>
            <AtlasResultsHeader
                count={count ?? 0}
                resultsLabel={count ? `${count} résultat${(count ?? 0) > 1 ? "s": ""}` : "Aucun résultat"}
                onResetFilters={ customHasActiveFilters() ? resetFilters : undefined }
                actions={ <CampusSort
                    currentOption={sortOptions.find(option => option.id === searchParams?.get("tri")) ?? sortOptions[0]}
                    options={sortOptions}
                    onClick={option => updateFilters({"tri": option.id === "date" ? undefined : option.id})}
                ></CampusSort>}
            />
            <AtlasFlex column gap="xxl">
                {loading ? <>
                    <CampusModuleWithStages.Skeleton />
                    <CampusModuleWithStages.Skeleton />
                    <CampusModuleWithStages.Skeleton />
                </> : data?.length
                    ? data.map((module) => <CampusModuleWithStages
                        key={module.sfid}
                        label={module.label}
                        category={<CampusTag>{module.thematique!.label}</CampusTag>}
                        tags={[
                            ...module.dureeFormation
                                ? [<CampusTag key="duration">{formatDuration(module.dureeFormation)}</CampusTag>]
                                : [],
                            <CofinancementTag key="cofinancement" cofinancement={ module.cofinancement ?? "" } />,
                        ]}
                        stages={(module.stages ?? [])?.map((stage) => getStageCard(module, stage))}
                        description={new DOMParser().parseFromString(module.objectif ?? "", 'text/html').body.textContent ?? ""}
                    />) : <AtlasInfo type="error" title="Aucun résultat" link={<NextLink href="/branches">Voir le catalogue des formations</NextLink>}>
                    Désolé, aucun module ne correspond à vos critères de recherche.
                </AtlasInfo>}
            </AtlasFlex>
        </AtlasLoading>
    </Filters>
}
